/**
 * Satın Alma — eski `procurement.php` + `procurement_post.php` karşılığı.
 *
 * Eski ekranın düzeltilen hataları:
 *  · `bind_param("isi")` fiyatı INTEGER'a kırpıyordu: "12,50" → 12. Ondalık
 *    ayırıcı dönüşümü de yoktu. Burada fiyat `parseKurus` ile virgüllü girilir,
 *    KURUŞ (integer) olarak gönderilir; sunucu ayrıca doğrular.
 *  · `success=` / `error=` parametreleri set ediliyor ama ekranda hiç
 *    okunmuyordu — kullanıcı kaydın olup olmadığını göremiyordu. Artık her
 *    sonuç (başarı ve hata) ekranda gösterilir.
 *  · Fiyat `erp_products.unitprice` üzerine yazılıyor, önceki fiyat kayboluyordu.
 *    Artık `components/{id}/private/pricing` sunucu tarafında güncellenir ve
 *    değişiklik denetim kaydına yazılır.
 *  · Tüm parçalar filtresiz tek sayfada listeleniyordu. Alış fiyatı ayrı bir
 *    alt dokümanda olduğu için burada kapsam ZORUNLU olarak daraltılır
 *    (kategori veya arama) — hem yetki hem performans gereği.
 *
 * Yetki: `costView` (boss + office). `production` bu ekranı göremez.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { where, orderBy, limit } from "firebase/firestore";
import { PageHeader, Spinner, EmptyState, useToast } from "../components";
import { useCollection, useDoc, useCallableAction } from "../hooks";
import { useCan } from "../lib/auth";
import { tl, n, date } from "../lib/format";
import { NoAccess } from "../components";
import type { FinanceSettings } from "./Settings";
import { ErrorNote, Field, MoneyInput } from "../components";
import type { ComponentCategory, ComponentDoc, ComponentPricing, UpsertComponentInput, UpsertComponentOutput } from "../lib/types";
import { parseKurus } from "../lib/decimal";

const INPUT =
  "w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500";

export default function Procurement() {
  const can = useCan();

  if (!can.seeCost) {
    return (
      <PageHeader title="Satın Alma">
        <NoAccess what="Alış fiyatları ve finans parametreleri" />
      </PageHeader>
    );
  }

  return (
    <PageHeader title="Satın Alma">
      <FinanceParameters />
      <ComponentPrices />
      <PriceChangeLog />
    </PageHeader>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 1. FİNANS PARAMETRELERİ — okuma costView, düzenleme YALNIZCA boss
// ═══════════════════════════════════════════════════════════════════════════

function FinanceParameters() {
  const can = useCan();
  const { data, loading, error } = useDoc<FinanceSettings>("settings/finance");

  return (
    <section className="mb-8">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-slate-800">Finans Parametreleri</h3>
          <p className="text-xs text-slate-500">
            Maliyet ve kâr hesabının tek kaynağı. Düzenleme yetkisi yalnızca iş sahibindedir.
          </p>
        </div>
        {can.isBoss ? (
          <Link
            to="/settings"
            className="rounded border border-slate-300 px-3 py-2 text-sm hover:bg-slate-100"
          >
            Ayarlarda düzenle
          </Link>
        ) : null}
      </header>

      <ErrorNote error={error} />
      {loading ? <Spinner /> : null}

      {data ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <Param label="KDV" value={`%${n(data.kdv)}`} />
          <Param label="Komisyon" value={`%${n(data.komisyon)}`} />
          <Param label="Shopify Komisyonu" value={`%${n(data.shopifyKomisyon)}`} />
          <Param label="Kargo (desi başı)" value={`${n(data.kargodesifiyat)} TL`} />
          <Param label="Desi Böleni" value={n(data.desiDivisor)} />
        </div>
      ) : null}
      {!loading && !data ? (
        <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Finans ayarları tanımlı değil. Maliyet hesabı çalışmaz.
        </p>
      ) : null}
    </section>
  );
}

function Param({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-slate-200 bg-white px-3 py-2">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-base font-semibold tabular-nums text-slate-800">{value}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 2. PARÇA ALIŞ FİYATLARI
// ═══════════════════════════════════════════════════════════════════════════

function ComponentPrices() {
  const { data: categories } = useCollection<ComponentCategory>("componentCategories", [
    where("deleted", "==", false),
    orderBy("order"),
  ]);

  const { data: components, loading, error } = useCollection<ComponentDoc>("components", [
    where("deleted", "==", false),
    where("active", "==", true),
    orderBy("nameLower"),
  ]);

  const [categoryId, setCategoryId] = useState("");
  const [q, setQ] = useState("");

  const categoryName = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of categories) m.set(c.id, c.name);
    return m;
  }, [categories]);

  const needle = q.trim().toLocaleLowerCase("tr");
  const scoped = categoryId !== "" || needle.length >= 2;

  const rows = useMemo(() => {
    if (!scoped) return [];
    return components.filter((c) => {
      if (categoryId && c.categoryId !== categoryId) return false;
      // Eski kural: adet birimli koliler satın alma listesinde gizlenir
      // (kolinin kendi alış fiyatı maliyete girmez, yalnızca desisi girer).
      if (c.isPackaging && c.unit === "adet") return false;
      if (!needle) return true;
      return (
        c.name.toLocaleLowerCase("tr").includes(needle) ||
        (c.externalLabel ?? "").toLocaleLowerCase("tr").includes(needle)
      );
    });
  }, [components, categoryId, needle, scoped]);

  return (
    <section>
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-slate-800">Parça Alış Fiyatları</h3>
          <p className="text-xs text-slate-500">
            Fiyat girerken Türkçe ondalık kullanın: <code>12,50</code>. Boş bırakılan satır
            değiştirilmez.
          </p>
        </div>
        <Link
          to="/components"
          className="rounded border border-slate-300 px-3 py-2 text-sm hover:bg-slate-100"
        >
          Parça Yönetimi
        </Link>
      </header>

      <div className="mb-3 grid grid-cols-1 gap-2 rounded border border-slate-200 bg-slate-50 p-3 md:grid-cols-3">
        <Field label="Kategori">
          <select className={INPUT} value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">Seçiniz…</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="md:col-span-2">
          <Field label="Ara (en az 2 karakter)">
            <input
              className={INPUT}
              value={q}
              placeholder="Parça adı veya dış etiket"
              onChange={(e) => setQ(e.target.value)}
            />
          </Field>
        </div>
      </div>

      <ErrorNote error={error} />
      {loading ? <Spinner /> : null}

      {!scoped ? (
        <EmptyState
          title="Önce kapsam seçin"
          description="Alış fiyatları korumalı alt dokümanlardadır; tüm katalog aynı anda açılmaz. Bir kategori seçin veya arama yapın."
        />
      ) : null}

      {scoped && rows.length === 0 && !loading ? (
        <EmptyState title="Parça bulunamadı" description="Kategori veya aramayı değiştirin." />
      ) : null}

      {rows.length > 0 ? (
        <div className="overflow-x-auto rounded border border-slate-200">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-3 py-2">Parça</th>
                <th className="px-3 py-2">Kategori</th>
                <th className="px-3 py-2">Birim</th>
                <th className="px-3 py-2 text-right">Mevcut Fiyat</th>
                <th className="px-3 py-2">Tedarikçi</th>
                <th className="px-3 py-2 w-56">Yeni Fiyat</th>
                <th className="px-3 py-2 w-24" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((c) => (
                <PriceRow
                  key={c.id}
                  component={c}
                  categoryLabel={c.categoryName ?? categoryName.get(c.categoryId) ?? "—"}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

/**
 * Tek satır. Alış fiyatı `components/{id}/private/pricing` alt dokümanından
 * AYRI okunur — parça dokümanında fiyat DURAMAZ (Rules `unitPrice` anahtarını
 * reddeder). Bu bileşen yalnızca `costView` olan kullanıcıda render edilir.
 */
function PriceRow({
  component,
  categoryLabel,
}: {
  component: ComponentDoc;
  categoryLabel: string;
}) {
  const toast = useToast();
  const { data: pricing, error } = useDoc<ComponentPricing>(
    `components/${component.id}/private/pricing`,
  );
  const upsert = useCallableAction<UpsertComponentInput, UpsertComponentOutput>("upsertComponent");

  const [priceText, setPriceText] = useState("");
  const [supplier, setSupplier] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [touchedSupplier, setTouchedSupplier] = useState(false);

  const currentSupplier = touchedSupplier ? supplier : pricing?.supplier ?? "";

  async function save() {
    setLocalError(null);

    // `kurus` YALNIZCA yerel doğrulama ve onay mesajı içindir.
    // Sunucuya HAM METİN gider: dönüşüm tek yerde, `toKurus()` içinde yapılır.
    // (Bir ara burada kuruşa çevrilip gönderiliyordu; sunucu bir kez daha
    // çevirdiği için 450 ₺ → 45.000 ₺ olarak kaydediliyordu.)
    const kurus = parseKurus(priceText);
    if (kurus === null) {
      setLocalError('Fiyat okunamadı. Ondalık için virgül kullanın (örnek: 12,50).');
      return;
    }
    if (kurus < 0) {
      setLocalError("Fiyat negatif olamaz.");
      return;
    }

    const res = await upsert.run({
      componentId: component.id,
      name: component.name,
      categoryId: component.categoryId,
      unit: component.unit,
      dimensions: {
        height: component.dimensions?.height ?? 0,
        width: component.dimensions?.width ?? 0,
        length: component.dimensions?.length ?? 0,
      },
      active: component.active !== false,
      isPackaging: component.isPackaging === true,
      isSelectableAsComponent: component.isSelectableAsComponent !== false,
      unitPrice: priceText,
      currency: pricing?.currency ?? "TRY",
      supplier: currentSupplier.trim(),
    });

    if (res) {
      toast.success(`${component.name}: fiyat ${tl(kurus)} olarak kaydedildi.`);
      setPriceText("");
    }
  }

  // Fiyat değişiminin yönünü kullanıcıya göster — eski ekranda hiç geri bildirim yoktu.
  const candidate = parseKurus(priceText);
  const delta =
    candidate !== null && pricing && pricing.unitPrice > 0
      ? ((candidate - pricing.unitPrice) / pricing.unitPrice) * 100
      : null;

  return (
    <tr className="align-top hover:bg-slate-50">
      <td className="px-3 py-2">
        <span className="font-medium text-slate-800">{component.name}</span>
        {component.externalLabel ? (
          <span className="block text-xs text-slate-600">{component.externalLabel}</span>
        ) : null}
      </td>
      <td className="px-3 py-2 text-slate-600">{categoryLabel}</td>
      <td className="px-3 py-2 text-slate-600">{component.unit}</td>
      <td className="px-3 py-2 text-right tabular-nums">
        {error ? (
          <span className="text-xs text-red-600">{error}</span>
        ) : pricing ? (
          <>
            {tl(pricing.unitPrice)}
            <span className="block text-xs text-slate-600">
              {date(pricing.lastPurchaseAt ?? pricing.updatedAt ?? null)}
            </span>
          </>
        ) : (
          <span className="text-xs text-amber-600">girilmemiş</span>
        )}
      </td>
      <td className="px-3 py-2">
        <input
          className={INPUT}
          value={currentSupplier}
          placeholder="Tedarikçi"
          onChange={(e) => {
            setSupplier(e.target.value);
            setTouchedSupplier(true);
          }}
        />
      </td>
      <td className="px-3 py-2">
        <MoneyInput
          value={priceText}
          onChange={setPriceText}
          aria-label={`${component.name} yeni fiyat`}
        />
        {delta !== null ? (
          <span
            className={`mt-1 block text-xs tabular-nums ${
              delta > 0 ? "text-red-600" : delta < 0 ? "text-emerald-700" : "text-slate-600"
            }`}
          >
            {delta > 0 ? "+" : ""}
            {n(Math.round(delta * 10) / 10)}%
          </span>
        ) : null}
        <ErrorNote error={localError ?? upsert.error} />
      </td>
      <td className="px-3 py-2">
        <button
          type="button"
          onClick={save}
          disabled={upsert.loading || !priceText.trim()}
          className="rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-40"
        >
          {upsert.loading ? "…" : "Kaydet"}
        </button>
      </td>
    </tr>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 3. ALIŞ FİYATI DEĞİŞİKLİK DEFTERİ — `procurement/{entryId}`
//
// ★ SUNUCU YAZIYORDU, HİÇBİR EKRAN OKUMUYORDU.
//
// `catalog/components.ts:priceChangeEntry` her alış fiyatı güncellemesinde
// eski fiyatı, yeni fiyatı ve değişim oranını buraya yazıyor. Bu dosyanın en
// üstündeki açıklama da "önceki fiyat kayboluyordu ... artık değişiklik kaydına
// yazılır" diyor — ama kayıt hiçbir yerde GÖSTERİLMİYORDU. Üstelik koleksiyonun
// Security Rules karşılığı da yoktu: bir ekran okumayı denese "permission
// denied" alırdı. Yani söz verilen tarihçe vardı, görünürlüğü yoktu.
//
// ── NİÇİN CANLI SORGU ─────────────────────────────────────────────────────
// Özet/sayaç dokümanı tutulmuyor: defterin kendisi okunuyor. Türetilmiş kopya
// bu projede defalarca gerçeğinden saptı (`counters/*`), burada sapacak bir
// kopya YOK — gösterilen satırlar kaydın ta kendisi.
//
// Yetki: `costView` (boss + office). Bileşen ALIŞ fiyatı taşır, üretim rolü
// bu ekranı zaten göremez; Rules de aynı koşulu bağımsız olarak uygular.
// ═══════════════════════════════════════════════════════════════════════════

interface PriceChangeEntry {
  id: string;
  entryType?: string;
  componentId?: string;
  componentName?: string;
  unit?: string;
  /** KURUŞ. İlk fiyat girişinde `oldValue` null'dır. */
  oldValue?: number | null;
  newValue?: number;
  changePct?: number;
  note?: string;
  at?: unknown;
  by?: string;
}

function PriceChangeLog() {
  // Sıralama alanı `at` canlıda her kayıtta dolu; eksik olsaydı o kayıt
  // `orderBy`dan sessizce düşer ve defter eksik görünürdü.
  const constraints = useMemo(() => [orderBy("at", "desc"), limit(50)], []);
  const { data, loading, error } = useCollection<PriceChangeEntry>("procurement", constraints);

  return (
    <section className="mt-8">
      <header className="mb-3">
        <h3 className="text-base font-semibold text-slate-800">Fiyat Değişiklikleri</h3>
        <p className="text-xs text-slate-500">
          Son 50 alış fiyatı değişimi. Kayıtlar sunucu tarafında, fiyat kaydedildiği anda
          açılır; buradan değiştirilemez.
        </p>
      </header>

      <ErrorNote error={error} />
      {loading ? <Spinner /> : null}

      {!loading && data.length === 0 ? (
        <EmptyState
          title="Henüz fiyat değişikliği yok"
          description="Bir parçanın alış fiyatı güncellendiğinde değişim burada listelenir."
        />
      ) : null}

      {data.length > 0 ? (
        <div className="overflow-x-auto rounded border border-slate-200">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-3 py-2">Tarih</th>
                <th className="px-3 py-2">Parça</th>
                <th className="px-3 py-2">Birim</th>
                <th className="px-3 py-2 text-right">Eski</th>
                <th className="px-3 py-2 text-right">Yeni</th>
                <th className="px-3 py-2 text-right">Değişim</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((e) => {
                const pctVal = typeof e.changePct === "number" ? e.changePct : null;
                return (
                  <tr key={e.id}>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{date(e.at)}</td>
                    <td className="px-3 py-2">
                      {e.componentId ? (
                        <Link to="/components" className="text-sky-700 hover:underline">
                          {e.componentName ?? e.componentId}
                        </Link>
                      ) : (
                        (e.componentName ?? "—")
                      )}
                      {e.note ? (
                        <span className="ml-2 text-xs text-slate-500">{e.note}</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{e.unit ?? "—"}</td>
                    {/* İlk fiyat girişinde eski fiyat YOK; sıfır yazmak "bedava alınıyordu"
                        demek olurdu — ayrım açıkça korunur. */}
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                      {e.oldValue === null || e.oldValue === undefined ? (
                        <span className="text-slate-400">ilk giriş</span>
                      ) : (
                        tl(e.oldValue)
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-medium tabular-nums text-slate-800">
                      {tl(e.newValue)}
                    </td>
                    <td
                      className={`px-3 py-2 text-right tabular-nums ${
                        pctVal === null || pctVal === 0
                          ? "text-slate-500"
                          : pctVal > 0
                            ? "text-red-700"
                            : "text-emerald-700"
                      }`}
                    >
                      {pctVal === null ? "—" : `${pctVal > 0 ? "+" : ""}${n(pctVal)}%`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
